import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { T, TONES, ddmon } from "../clientsTokens";
import { quoteEmailPrefill } from "../../../../lib/quoteEmail";
import { Card, CardHead, Secondary, Pill, Tag, Dash, DotsMenu } from "./recordBits";
import { formatMoney0 } from "../../../../lib/money";
import { AUTHORITY_OPTIONS } from "../../ContactsEditor";

const AUTHORITY_LABEL = Object.fromEntries(AUTHORITY_OPTIONS.map((o) => [o.value, o.label]));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CS = `${API}/customer-success`;
const money = (n, cur = "AUD") => formatMoney0(n, cur);
const Empty = ({ children, testid }) => (
  <div className="px-4 py-5 text-[13px] font-semibold" style={{ color: T.ink }} data-testid={testid}>{children}</div>
);

/* §25 — right rail, five cards in order. */

export const ContactsCard = ({ clientId, contacts, onChanged, onEdit, onAdd }) => {
  const [showInactive, setShowInactive] = useState(false);
  const active = contacts.filter((c) => c.active !== false);
  const inactive = contacts.filter((c) => c.active === false);
  const shown = showInactive ? [...active, ...inactive] : active;

  const remove = async (c) => {
    try {
      await axios.delete(`${CS}/clients/${encodeURIComponent(clientId)}/contacts/${c.id}`);
      toast.success("Contact removed"); onChanged();
    } catch { toast.error("Could not remove that contact"); }
  };
  const setActive = async (c, next) => {
    try {
      await axios.post(`${CS}/clients/${encodeURIComponent(clientId)}/contacts/${c.id}/active`,
        { active: next });
      toast.success(next ? `${c.name || c.email} is active again`
        : `${c.name || c.email} marked inactive`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change that contact"); }
  };
  const makePrimary = async (c) => {
    try {
      await axios.post(`${CS}/clients/${encodeURIComponent(clientId)}/contacts/${c.id}/primary`);
      toast.success(`${c.name || c.email} is now the primary contact`); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set the primary contact"); }
  };

  const phones = (c) => [
    { value: c.phone_office || c.phone, label: "office" },
    { value: c.phone_mobile, label: "mobile" },
  ].filter((p) => p.value);

  return (
    <Card testid="client-contacts">
      <CardHead title="Contacts" count={active.length}
        action={<Secondary onClick={onAdd} testid="contacts-add">Add</Secondary>} />
      {shown.length === 0 ? <Empty testid="contacts-empty">No contacts on record.</Empty> : shown.map((c) => {
        const off = c.active === false;
        return (
        <div key={c.id} className="grid px-4 py-2.5 items-start" data-testid={`contact-row-${c.id}`}
          style={{ gridTemplateColumns: "minmax(0,1fr) 30px", borderBottom: `1px solid ${T.dividerRow}`,
            opacity: off ? 0.55 : 1 }}>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              {c.name ? (
                <span className="text-[13px] font-semibold" style={{ color: T.ink }}>{c.name}</span>
              ) : (
                <span className="text-[12.5px] italic" style={{ color: T.ink2 }}>Name missing</span>
              )}
              {c.role && <span className="text-[12px]" style={{ color: T.ink2 }}>{c.role}</span>}
              {c.is_primary && <Tag>Primary</Tag>}
              {off && (
                <span className="px-1.5 py-0.5 rounded-[5px] text-[10.5px] font-bold uppercase tracking-wide"
                  data-testid={`contact-inactive-${c.id}`}
                  style={{ background: T.pressed, color: T.ink2, border: `1px solid ${T.control}` }}>
                  Inactive
                </span>
              )}
              {c.authority && (
                <span className="px-1.5 py-0.5 rounded-[5px] text-[10.5px] font-bold uppercase tracking-wide"
                  data-testid={`contact-authority-${c.id}`}
                  style={{ background: "#FFF8D6", color: "#8A6D00", border: "1px solid #F0E2A8" }}>
                  {AUTHORITY_LABEL[c.authority] || c.authority}
                </span>
              )}
            </div>
            {c.email && (
              <a href={`mailto:${c.email}`} className="block text-[12.5px] truncate" style={{ color: T.accentInk }}>{c.email}</a>
            )}
            {phones(c).map((p) => (
              <div key={p.label} className="flex items-center gap-1.5">
                <a href={`tel:${String(p.value).replace(/[^+\d]/g, "")}`} className="mono text-[11.5px]"
                  style={{ color: T.ink }} data-testid={`contact-${p.label}-${c.id}`}>{p.value}</a>
                <span className="text-[11.5px]" style={{ color: T.ink2 }}>{p.label}</span>
              </div>
            ))}
          </div>
          <DotsMenu testid={`contact-menu-${c.id}`} items={[
            { label: "Edit contact", onClick: () => onEdit(c), testid: `contact-edit-${c.id}` },
            !c.is_primary && !off && { label: "Make primary", hint: "The record header reads from the primary contact.",
              onClick: () => makePrimary(c), testid: `contact-primary-${c.id}` },
            off
              ? { label: "Make active", hint: "Puts them back in the contact list.",
                  onClick: () => setActive(c, true), testid: `contact-activate-${c.id}` }
              : { label: "Mark inactive", hint: "They have left or no longer deal with us. Kept on the record, out of the list.",
                  onClick: () => setActive(c, false), testid: `contact-deactivate-${c.id}` },
            { label: "Remove contact", danger: true, testid: `contact-remove-${c.id}`,
              hint: "Deletes the contact from this client. Use Mark inactive to keep the history.",
              onClick: () => remove(c) },
          ]} />
        </div>
        );
      })}
      {inactive.length > 0 && (
        <button onClick={() => setShowInactive((v) => !v)} data-testid="contacts-show-inactive"
          className="w-full px-4 py-2 text-left text-[11.5px] font-semibold"
          style={{ color: T.ink2, background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
          {showInactive ? "Hide" : "Show"} {inactive.length} inactive
        </button>
      )}
    </Card>
  );
};

export const QuotesCard = ({ clientId, quotes, onChanged }) => {
  const navigate = useNavigate();
  const email = (q) => {
    const route = quoteEmailPrefill(q, { linkType: "client", linkId: clientId });
    if (!route) { toast.error("This quote isn't linked to a client"); return; }
    navigate(route.dest, { state: route.state });
  };
  const reissue = async (q) => {
    try {
      const r = await axios.post(`${API}/quotes/${q.id}/duplicate`);
      toast.success("Copied into a new draft");
      onChanged();
      const id = r.data?.quote?.id;
      if (id) window.location.href = `/quotes/${id}`;
    } catch { toast.error("Could not copy that quote"); }
  };
  const del = async (q) => {
    try { await axios.delete(`${API}/quotes/${q.id}`); toast.success("Quote deleted"); onChanged(); }
    catch { toast.error("Could not delete that quote"); }
  };

  return (
    <Card testid="client-quotes">
      <CardHead title="Quotes" count={quotes.length}
        action={<Secondary href={`/quotes/new?link_type=client&link_id=${encodeURIComponent(clientId)}`} testid="quotes-new">New quote</Secondary>} />
      {quotes.length === 0 ? <Empty testid="quotes-empty">No quotes for this client.</Empty> : quotes.map((q) => {
        const expired = q.status === "expired";
        const total = q.totals?.one_off?.total ?? q.totals?.total ?? q.hs_amount;
        return (
          <div key={q.id} className="px-4 py-3" data-testid={`quote-row-${q.id}`}
            style={{ borderBottom: `1px solid ${T.dividerRow}` }}>
            <div className="flex items-center gap-2">
              <a href={`/quotes/${q.id}`} className="mono text-[11.5px] font-bold truncate" style={{ color: T.accentInk }}>
                {q.quote_number}
              </a>
              <Pill text={(q.status || "draft").replace(/^\w/, (m) => m.toUpperCase())} tone={expired ? "zero" : "neutral"} />
              <div className="ml-auto">
                <DotsMenu testid={`quote-menu-${q.id}`} items={[
                  { label: "Open quote", onClick: () => { window.location.href = `/quotes/${q.id}`; } },
                  expired
                    ? { label: "Reissue as new quote", hint: "Copies the lines into a new draft. Nothing is sent.",
                        onClick: () => reissue(q), testid: `quote-reissue-${q.id}` }
                    : { label: `Email ${q.contact_name || "the contact"}`,
                        hint: `Opens the email for you to read before it goes to ${q.contact_email || "the contact"}.`,
                        onClick: () => email(q), testid: `quote-resend-${q.id}` },
                  { label: "Download PDF", onClick: () => window.open(`${API}/quotes/${q.id}/pdf`, "_blank") },
                  { label: "Copy link", onClick: () => {
                      navigator.clipboard?.writeText(`${window.location.origin}/quotes/${q.id}`);
                      toast.success("Link copied");
                    } },
                  { label: "Delete quote", danger: true, testid: `quote-delete-${q.id}`,
                    hint: "Removes the quote and its history. Cannot be undone.", onClick: () => del(q) },
                ]} />
              </div>
            </div>
            <div className="mono text-[14px] font-bold mt-0.5" style={{ color: T.ink }}>{money(total, q.currency)}</div>
            <div className="text-[11.5px]" style={{ color: T.ink2 }}>
              {q.contact_name || "No contact"} · valid to <span className="mono">{ddmon(q.valid_until) || "—"}</span>
            </div>
          </div>
        );
      })}
    </Card>
  );
};

export const JourneyCard = ({ clientId, journey, onStart }) => {
  const stages = journey?.exists ? journey.stages || [] : [];
  const idx = stages.findIndex((s) => !s.completed);
  const current = idx === -1 ? stages.length : idx + 1;

  return (
    <Card testid="client-journey">
      <CardHead title="Journey"
        action={stages.length
          ? <Secondary href={`/customer-success/journey?client=${encodeURIComponent(clientId)}`} testid="journey-open">Open journey</Secondary>
          : <Secondary onClick={onStart} testid="journey-start">Start journey</Secondary>} />
      {stages.length === 0 ? <Empty testid="journey-empty">No journey stages recorded.</Empty> : (
        <div className="px-4 py-3.5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[13px] font-semibold" style={{ color: T.ink }}>
              {current > stages.length ? "Bedded in" : `${current}. ${stages[current - 1]?.name}`}
            </span>
            <span className="mono text-[11.5px]" style={{ color: T.ink2 }}>
              {stages.filter((s) => s.completed).length}/{stages.length} done
            </span>
          </div>
          <div className="flex gap-[2px]">
            {stages.map((s, i) => (
              <span key={s.key || i} title={`${i + 1}. ${s.name}`} className="flex-1"
                style={{ height: 8, borderRadius: i === 0 ? "4px 0 0 4px" : i === stages.length - 1 ? "0 4px 4px 0" : 0,
                  background: s.completed ? "#6B5600" : i + 1 === current ? T.accent : T.pressed }} />
            ))}
          </div>
          {journey?.target_go_live && (
            <div className="text-[11.5px] mt-2" style={{ color: T.ink2 }}>
              Target go-live <span className="mono">{ddmon(journey.target_go_live)}</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export const TicketsCard = ({ jiraOrg, openCount }) => (
  <Card testid="client-tickets">
    <CardHead title="Tickets" count={`${openCount || 0} open`}
      action={<Secondary testid="tickets-jira" title="Opens the Jira organisation in a new tab"
        onClick={() => window.open(`https://equipr.atlassian.net/jira/servicedesk/projects?q=${encodeURIComponent(jiraOrg || "")}`, "_blank")}>
        Open in Jira</Secondary>} />
    <Empty testid="tickets-body">{openCount ? `${openCount} open in Jira.` : "No open tickets."}</Empty>
  </Card>
);

export const RecordCard = ({ client, onEdit }) => {
  const fields = [
    { label: "Display name", value: client.company_name },
    { label: "Entitlements", value: [...(client.has_sla ? ["SLA"] : []), ...(client.products || [])].join(" · ") },
    { label: "Allocate version", value: client.allocate_version, mono: true },
    { label: "Last system update", value: ddmon(client.allocate_last_update), mono: true },
    { label: "Client since", value: ddmon(client.client_since), mono: true },
    { label: "Last synced", value: ddmon(client.last_synced), mono: true },
  ];
  return (
    <Card testid="client-record-card">
      <CardHead title="Record" action={<Secondary onClick={onEdit} testid="record-edit">Edit</Secondary>} />
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))",
        gap: 1, background: T.dividerInner }}>
        {fields.map((f) => (
          <div key={f.label} className="px-4 py-3" style={{ background: T.card }}>
            <div className="text-[10.5px] font-bold uppercase" style={{ letterSpacing: "0.09em", color: T.muted }}>{f.label}</div>
            <div className={f.mono ? "mono text-[11.5px] mt-0.5" : "text-[12.5px] mt-0.5"} style={{ color: T.ink }}>
              {f.value || <Dash />}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
